/**
 * FarmRoadService - Service d'intégration avec l'API FarmRoad Public
 *
 * Doc : https://developer.farmroad.io/
 *
 * Tous les appels passent par le proxy backend (quiz-api) pour éviter
 * les problèmes CORS et ne pas exposer la clé API au navigateur.
 * Base URL backend : REACT_APP_BACKEND_URL/api/farmroad
 *
 * Endpoints utilisés (via proxy) :
 *   GET  /farms          → Liste des fermes accessibles
 *   GET  /farms/{id}     → Détail d'une ferme (Compartments, Sectors)
 *   GET  /measurements   → Récupération des mesures (capteurs, irrigation, climat)
 *
 * Authentification : gérée côté backend avec FARMROAD_API_KEY (process.env)
 *                    + Firebase Auth token pour les routes protégées
 */

import { getFirebaseToken } from '../firebase';

class FarmRoadService {
  constructor() {
    // Lu à chaque appel via _getBaseUrl() pour respecter le même pattern
    // que process.env dans un singleton (résolu par le bundler au build time).
  }

  _getBaseUrl() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    return `${backendUrl}/api/farmroad`;
  }

  // ---------------------------------------------------------------------------
  // Headers communs à toutes les requêtes vers le proxy backend.
  // Inclut le token Firebase pour l'authentification des routes protégées.
  // ---------------------------------------------------------------------------
  async _headers() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Récupérer le token Firebase si l'utilisateur est connecté
    try {
      const token = await getFirebaseToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Impossible de récupérer le token Firebase:', error);
      // Continuer sans token - certaines routes peuvent ne pas nécessiter d'authentification
    }

    return headers;
  }

  // ---------------------------------------------------------------------------
  // Gestion des réponses HTTP — parsing uniforme + messages d'erreur clairs
  // ---------------------------------------------------------------------------
  async _handleResponse(response, context = '') {
    if (response.ok) {
      return await response.json();
    }

    let errorMessage = `FarmRoad API [${context}] — HTTP ${response.status}`;

    try {
      const errorBody = await response.json();
      // Le format d'erreur FarmRoad suit le schéma ErrorResponse
      if (errorBody.message) {
        errorMessage += `: ${errorBody.message}`;
      } else if (errorBody.error) {
        errorMessage += `: ${errorBody.error}`;
      }
    } catch {
      // Si le body n'est pas du JSON, on garde juste le statusText
      errorMessage += `: ${response.statusText}`;
    }

    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  // ===========================================================================
  // FERMES — Farm layout
  // ===========================================================================

  /**
   * Récupère la liste de toutes les fermes accessibles avec la clé API.
   * Chaque ferme contient ses Compartments et Sectors.
   * @returns {Promise<Array>} Liste des objets Farm
   */
  async getFarms() {
    const response = await fetch(`${this._getBaseUrl()}/farms`, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'GET /farms');
  }

  /**
   * Récupère le détail d'une ferme par son ID.
   * Inclut la structure complète : Compartments → Sectors → Devices.
   * @param {string} farmId - Identifiant de la ferme
   * @returns {Promise<Object>} Objet Farm avec ses sous-éléments
   */
  async getFarmById(farmId) {
    if (!farmId) {
      throw new Error('FarmRoadService.getFarmById : farmId est requis');
    }

    const response = await fetch(`${this._getBaseUrl()}/farms/${farmId}`, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, `GET /farms/${farmId}`);
  }

  /**
   * Récupère tous les compartiments d'une ferme depuis la base locale.
   * @param {string} farmId - Identifiant de la ferme
   * @returns {Promise<Array>} Liste des compartiments
   */
  async getCompartments(farmId) {
    if (!farmId) {
      throw new Error('FarmRoadService.getCompartments : farmId est requis');
    }

    const response = await fetch(`${this._getBaseUrl()}/farms/${farmId}/compartments`, {
      method: 'GET',
      headers: await this._headers()
    });

    const result = await this._handleResponse(response, `GET /farms/${farmId}/compartments`);
    return result.data || [];
  }

  /**
   * Récupère tous les secteurs d'une ferme depuis la base locale.
   * @param {string} farmId - Identifiant de la ferme
   * @param {string} [compartmentId] - ID du compartiment pour filtrer (optionnel)
   * @returns {Promise<Array>} Liste des secteurs
   */
  async getSectors(farmId, compartmentId = null) {
    if (!farmId) {
      throw new Error('FarmRoadService.getSectors : farmId est requis');
    }

    let url = `${this._getBaseUrl()}/farms/${farmId}/sectors`;
    if (compartmentId) {
      url += `?compartmentId=${compartmentId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: await this._headers()
    });

    const result = await this._handleResponse(response, `GET /farms/${farmId}/sectors`);
    return result.data || [];
  }

  /**
   * Synchronise la structure complète d'une ferme (compartiments + secteurs)
   * depuis l'API FarmRoad vers PostgreSQL.
   * @param {string} farmId - Identifiant de la ferme
   * @returns {Promise<Object>} Statistiques de synchronisation
   */
  async syncStructure(farmId) {
    if (!farmId) {
      throw new Error('FarmRoadService.syncStructure : farmId est requis');
    }

    const response = await fetch(`${this._getBaseUrl()}/farms/${farmId}/sync-structure`, {
      method: 'POST',
      headers: await this._headers()
    });

    return this._handleResponse(response, `POST /farms/${farmId}/sync-structure`);
  }

  // ===========================================================================
  // MESURES — Measurements
  // ===========================================================================

  /**
   * Récupère les mesures depuis le cache PostgreSQL (lecture seule).
   * Cette méthode est ULTRA-RAPIDE car elle lit directement depuis la base locale.
   *
   * @param {Object} params - Paramètres de filtrage
   * @param {string} [params.farmId]        - Filtrer par ferme (REQUIS)
   * @param {string} [params.compartmentId] - Filtrer par compartiment
   * @param {string} [params.sector_id]     - Filtrer par secteur
   * @param {string} [params.sourceId]      - Filtrer par source (capteur/dispositif)
   * @param {string} [params.type]          - Type de mesure
   * @param {string} [params.startTime]     - Date de début ISO 8601
   * @param {string} [params.endTime]       - Date de fin ISO 8601
   * @returns {Promise<Array>} Tableau de mesures depuis PostgreSQL
   */
  async getMeasurementsFromCache(params = {}) {
    const queryString = this._buildQueryString(params);
    const url = `${this._getBaseUrl()}/db/measurements${queryString}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'GET /db/measurements (cache)');
  }

  /**
   * Synchronise les mesures depuis l'API FarmRoad vers PostgreSQL.
   * Cette opération met à jour automatiquement le cache PostgreSQL.
   * Opération BLOQUANTE : attend que toutes les données soient synchronisées.
   *
   * @param {Object} params - Paramètres de synchronisation
   * @param {string} [params.farmId]        - Filtrer par ferme (REQUIS)
   * @param {string} [params.compartmentId] - Filtrer par compartiment
   * @param {string} [params.sector_id]     - Filtrer par secteur
   * @param {string} [params.sourceId]      - Filtrer par source
   * @param {string} [params.type]          - Type de mesure
   * @param {string} [params.startTime]     - Date de début ISO 8601 (REQUIS)
   * @param {string} [params.endTime]       - Date de fin ISO 8601 (REQUIS)
   * @returns {Promise<Array>} Tableau de mesures fraîchement synchronisées
   */
  async syncFromAPI(params = {}) {
    const { farmId, startTime, endTime } = params;

    if (!farmId || !startTime || !endTime) {
      throw new Error('FarmRoadService.syncFromAPI : farmId, startTime et endTime sont requis');
    }

    const queryString = this._buildQueryString(params);
    const url = `${this._getBaseUrl()}/db/sync${queryString}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'POST /db/sync');
  }

  /**
   * Récupère le statut du cache PostgreSQL pour une ferme.
   *
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Object>} Statistiques du cache (count, oldest, newest, types, sources, compartments)
   */
  async getCacheStatus(farmId) {
    if (!farmId) {
      throw new Error('FarmRoadService.getCacheStatus : farmId requis');
    }

    const response = await fetch(`${this._getBaseUrl()}/debug/cache-status?farmId=${farmId}`, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'GET /debug/cache-status');
  }

  /**
   * Récupère les mesures depuis la plateforme FarmRoad.
   *
   * Le backend gère automatiquement le téléchargement des fichiers NDJSON
   * depuis CloudFront et le mapping vers le format du dashboard.
   *
   * Limites API : une requête couvre au maximum 1 heure de données.
   *
   * @param {Object} params - Paramètres de filtrage
   * @param {string} [params.farmId]        - Filtrer par ferme
   * @param {string} [params.compartmentId] - Filtrer par compartiment
   * @param {string} [params.sectorId]      - Filtrer par secteur
   * @param {string} [params.sourceId]      - Filtrer par source (capteur/dispositif)
   * @param {string} [params.type]          - Type de mesure (ex : measurement_type)
   * @param {string} [params.startTime]     - Date de début ISO 8601
   * @param {string} [params.endTime]       - Date de fin ISO 8601 (max startTime + 1h)
   * @returns {Promise<Array>} Tableau de mesures mappées
   */
  async getMeasurements(params = {}) {
    const queryString = this._buildQueryString(params);
    const url = `${this._getBaseUrl()}/measurements${queryString}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'GET /measurements');
  }

  /**
   * Récupère les mesures sur une plage de temps arbitraire en découpage automatique
   * par tranches d'1 heure (limite de l'API FarmRoad).
   *
   * @param {Object} params - Même structure que getMeasurements
   * @param {string} params.startTime - Date de début ISO 8601
   * @param {string} params.endTime   - Date de fin ISO 8601 (peut dépasser 1h)
   * @returns {Promise<Array>} Liste agrégée de toutes les mesures sur la plage
   */
  async getMeasurementsRange(params = {}) {
    const { startTime, endTime, ...otherParams } = params;

    if (!startTime || !endTime) {
      throw new Error('FarmRoadService.getMeasurementsRange : startTime et endTime sont requis');
    }

    const start = new Date(startTime);
    const end = new Date(endTime);
    const allMeasurements = [];

    let chunkStart = new Date(start);

    while (chunkStart < end) {
      // Chaque chunk = max 1 heure
      const chunkEnd = new Date(Math.min(chunkStart.getTime() + 3600000, end.getTime()));

      const result = await this.getMeasurements({
        ...otherParams,
        startTime: chunkStart.toISOString(),
        endTime: chunkEnd.toISOString()
      });

      // Le backend retourne directement un tableau de mesures
      if (Array.isArray(result)) {
        allMeasurements.push(...result);
      }

      chunkStart = chunkEnd;
    }

    return allMeasurements;
  }

  /**
   * Récupère les mesures sur une grande plage (jusqu'à 1 an) avec parallélisme
   * contrôlé par lots. Conçu pour ne pas saturer l'API ni le navigateur.
   *
   * @param {Object}   params            - Paramètres de filtrage (même que getMeasurements)
   * @param {string}   params.startTime  - Date de début ISO 8601
   * @param {string}   params.endTime    - Date de fin ISO 8601
   * @param {Object}   [options]         - Options de contrôle
   * @param {number}   [options.batchSize=10]      - Nombre de requêtes simultanées par lot
   * @param {Function} [options.onProgress]        - Callback (progression: 0→1, récupérées: number, total: number)
   * @param {AbortSignal} [options.signal]         - Signal pour annuler la récupération
   * @returns {Promise<Array>} Liste agrégée de toutes les mesures sur la plage
   */
  async getMeasurementsParallel(params = {}, options = {}) {
    const { startTime, endTime, ...otherParams } = params;
    const { batchSize = 10, onProgress, signal } = options;

    if (!startTime || !endTime) {
      throw new Error('FarmRoadService.getMeasurementsParallel : startTime et endTime sont requis');
    }

    const start = new Date(startTime);
    const end   = new Date(endTime);

    // -----------------------------------------------------------------------
    // 1. Générer tous les chunks d'1h
    // -----------------------------------------------------------------------
    const chunks = [];
    let cursor = new Date(start);
    while (cursor < end) {
      const chunkEnd = new Date(Math.min(cursor.getTime() + 3600000, end.getTime()));
      chunks.push({ start: new Date(cursor), end: chunkEnd });
      cursor = chunkEnd;
    }

    const totalChunks = chunks.length;
    let completedChunks = 0;
    const allMeasurements = [];

    // -----------------------------------------------------------------------
    // 2. Fonction qui récupère un seul chunk
    // -----------------------------------------------------------------------
    const fetchChunk = async (chunk) => {
      if (signal?.aborted) return [];

      const result = await this.getMeasurements({
        ...otherParams,
        startTime: chunk.start.toISOString(),
        endTime:   chunk.end.toISOString()
      });

      // Le backend retourne directement un tableau de mesures
      if (Array.isArray(result)) return result;
      return [];
    };

    // -----------------------------------------------------------------------
    // 3. Exécution par lots de `batchSize` requêtes parallèles
    // -----------------------------------------------------------------------
    for (let i = 0; i < totalChunks; i += batchSize) {
      if (signal?.aborted) {
        console.warn('FarmRoadService : récupération annulée par l\'utilisateur.');
        break;
      }

      const batch = chunks.slice(i, i + batchSize);

      const results = await Promise.all(
        batch.map((chunk) => fetchChunk(chunk).catch((err) => {
          console.warn(`FarmRoad : échec du chunk ${chunk.start.toISOString()} — ${err.message}`);
          return []; // on continue même si un chunk échoue
        }))
      );

      // Collecter les résultats du lot
      for (const measurements of results) {
        allMeasurements.push(...measurements);
      }

      // Mettre à jour la progression
      completedChunks += batch.length;
      if (onProgress) {
        onProgress({
          progression: Math.min(completedChunks / totalChunks, 1),
          recuperees: allMeasurements.length,
          chunksCompleted: completedChunks,
          chunksTotal: totalChunks
        });
      }
    }

    // Tri chronologique final
    allMeasurements.sort((a, b) => {
      const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tA - tB;
    });

    return allMeasurements;
  }

  // ===========================================================================
  // Utilitaires privés
  // ===========================================================================

  /**
   * Construit une query string à partir d'un objet de paramètres.
   * Les valeurs null/undefined sont ignorées.
   */
  _buildQueryString(params) {
    const filtered = Object.entries(params).filter(([, value]) => value != null);
    if (filtered.length === 0) return '';
    const qs = new URLSearchParams(filtered).toString();
    return `?${qs}`;
  }

  // ===========================================================================
  // CONFIGURATION CLÉ API (MULTI-TENANT)
  // ===========================================================================

  /**
   * Vérifie si l'utilisateur a configuré sa clé API FarmRoad.
   * @returns {Promise<{success: boolean, hasKey: boolean}>}
   */
  async checkApiKeyStatus() {
    const response = await fetch(`${this._getBaseUrl()}/key-status`, {
      method: 'GET',
      headers: await this._headers()
    });

    return this._handleResponse(response, 'GET /key-status');
  }

  /**
   * Enregistre ou met à jour la clé API FarmRoad de l'utilisateur.
   * Valide la clé en tentant de récupérer les fermes.
   * @param {string} apiKey - Clé API FarmRoad de l'utilisateur
   * @returns {Promise<{success: boolean, message: string, farmsCount: number}>}
   */
  async configureApiKey(apiKey) {
    const response = await fetch(`${this._getBaseUrl()}/configure-key`, {
      method: 'POST',
      headers: await this._headers(),
      body: JSON.stringify({ apiKey })
    });

    return this._handleResponse(response, 'POST /configure-key');
  }
}

// Export d'une instance singleton — même pattern que PerplexityService
export default new FarmRoadService();
